import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import useLocalStorage from 'use-local-storage';
import { getAllUserPredictions, getDivisionStandings, getMatchesForTeamBySeason } from '../../services/MatchService';
import { getAccountBySteamId } from '../../services/FantasyService';
import { StandingsPrediction } from '../../models/StandingsPrediction';
import { Account } from '../../models/Account';
import { currentSeason, divisionMappings } from '../../Constants';
import { Standing } from '../../models/Standing';
import { Match } from '../../models/Match';
import { PlayerStats } from '../../models/PlayerStats';
import PlayerImage from '../../components/PlayerImage';
import MatchTable from '../../components/MatchTabel';
import axios from 'axios';
import { baseURL } from "../../Constants";

// IDs of the OGC podcasters
const OGC_PODCASTER_IDS = [
  '76561197983673093', // P0se
  '76561198205544503'  // Broken
];

// Tabs for team details
const tabs = [
  { id: 'players', title: 'Spillere' },
  { id: 'matches', title: 'Kampe' }
];

interface TeamDetailProps {
  teamId: string;
  season: number;
  isDark: boolean;
}

const OGCPage: React.FC = () => {
  const [isDark] = useLocalStorage<boolean>('isDark', false);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [predictions, setPredictions] = useState<Map<string, StandingsPrediction[]>>(new Map());
  const [selectedSeason, setSelectedSeason] = useState<number>(currentSeason);
  const [selectedDivision, setSelectedDivision] = useState<string>('');
  const [divisions, setDivisions] = useState<string[]>([]);
  const [standings, setStandings] = useState<Standing[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Team details state
  const [selectedTeam, setSelectedTeam] = useState<Standing | null>(null);
  const [activeTab, setActiveTab] = useState<string>('players');
  const [teamPlayers, setTeamPlayers] = useState<PlayerStats[]>([]);
  const [teamMatches, setTeamMatches] = useState<Match[]>([]);
  const [teamDetailsLoading, setTeamDetailsLoading] = useState(false);
  
  // Sorting state
  const [sortedBy, setSortedBy] = useState<string>('rank');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  // Fetch accounts for both podcasters
  useEffect(() => {
    const fetchAccounts = async () => {
      try {
        setLoading(true);
        const accountPromises = OGC_PODCASTER_IDS.map(id => getAccountBySteamId(id));
        const accountResults = await Promise.all(accountPromises);
        setAccounts(accountResults.filter(Boolean));
      } catch (err) {
        console.error('Error fetching accounts:', err);
        setError('Could not load podcaster information');
      } finally {
        setLoading(false);
      }
    };

    fetchAccounts();
  }, []);

  // Fetch predictions for both podcasters
  useEffect(() => {
    const fetchAllPredictions = async () => {
      try {
        setLoading(true);
        const predictionPromises = OGC_PODCASTER_IDS.map(id => 
          getAllUserPredictions(id, selectedSeason)
        );
        const predictionResults = await Promise.all(predictionPromises);
        
        // Create a map of steamId -> predictions
        const predictionMap = new Map<string, StandingsPrediction[]>();
        OGC_PODCASTER_IDS.forEach((id, index) => {
          predictionMap.set(id, predictionResults[index] || []);
        });
        
        setPredictions(predictionMap);
        
        // Extract unique divisions from all predictions
        const allDivisions = new Set<string>();
        predictionResults.forEach(userPredictions => {
          userPredictions.forEach(prediction => {
            if (prediction.division) {
              allDivisions.add(prediction.division);
            }
          });
        });
        
        // Convert set to array and sort
        const sortedDivisions = Array.from(allDivisions).sort((a, b) => {
          if (a === "ligaen") return -1;
          if (b === "ligaen") return 1;
          return a.localeCompare(b);
        });
        
        setDivisions(sortedDivisions);
        
        // Set default division if none selected
        if (!selectedDivision && sortedDivisions.length > 0) {
          setSelectedDivision(sortedDivisions[0]);
        }
      } catch (err) {
        console.error('Error fetching predictions:', err);
        setError('Could not load predictions');
      } finally {
        setLoading(false);
      }
    };

    fetchAllPredictions();
  }, [selectedSeason]);

  // Fetch current standings for the selected division
  useEffect(() => {
    const fetchStandings = async () => {
      if (!selectedDivision) return;
      
      try {
        setLoading(true);
        const standingsData = await getDivisionStandings(selectedSeason, selectedDivision);
        const firstKey = Object.keys(standingsData)[0] || "";
        const divisionStandings: Standing[] = standingsData[firstKey] || [];
        setStandings(divisionStandings);
        
        // Auto-select the first team if we have standings and no team is selected
        if (divisionStandings.length > 0 && !selectedTeam) {
          setSelectedTeam(divisionStandings[0]);
        }
      } catch (err) {
        console.error('Error fetching standings:', err);
        setError('Could not load current standings');
      } finally {
        setLoading(false);
      }
    };
    
    fetchStandings();
  }, [selectedDivision, selectedSeason]);

  // Fetch team details (players and matches) when a team is selected
  useEffect(() => {
    const fetchTeamDetails = async () => {
      if (!selectedTeam) return;
      
      setTeamDetailsLoading(true);
      try {
        // Fetch team matches
        const matches = await getMatchesForTeamBySeason(selectedTeam.team.teamId, selectedSeason);
        setTeamMatches(matches);
        
        // Fetch team players directly using API
        try {
          const response = await axios.get(
            `${baseURL}/PlayerStat/getPlayersByTeamId?teamid=${selectedTeam.team.teamId}&season=${selectedSeason}`
          );
          if (response.data) {
            setTeamPlayers(response.data);
          }
        } catch (err) {
          console.error('Error fetching players:', err);
          setTeamPlayers([]);
        }
      } catch (err) {
        console.error('Error fetching team details:', err);
      } finally {
        setTeamDetailsLoading(false);
      }
    };
    
    fetchTeamDetails();
  }, [selectedTeam, selectedDivision, selectedSeason]);

  const getPredictionForDivision = (steamId: string, division: string): StandingsPrediction | undefined => {
    const userPredictions = predictions.get(steamId) || [];
    return userPredictions.find(p => p.division === division);
  };

  // Get prediction rank for a team from a specific user
  const getPredictionRank = (teamId: string, steamId: string): number | null => {
    const prediction = getPredictionForDivision(steamId, selectedDivision);
    if (!prediction) return null;
    
    const teamPrediction = prediction.teams.find(t => t.teamId === teamId);
    return teamPrediction ? teamPrediction.predictedRank : null;
  };

  // Get color for standing based on rank (promotion, relegation, etc.)
  const getStandingColor = (division: string, rank: number): string => {
    let color = '';
    
    if (division === "ligaen") {
      if (rank <= 4) color = "green";
      else if (rank === 7) color = "orange";
      else if (rank === 8) color = "red";
    } else if (division.startsWith("2div") || division.endsWith("div")) {
      if (rank === 1) color = "green";
      else if (rank === 2) color = "yellow";
      else if (rank === 7 || rank === 8) color = "orange";
      else if (rank >= 9 && rank <= 11) color = "red";
    } else if (division.startsWith("3div")) {
      if (rank === 1) color = "green";
      else if (rank >= 2 && rank <= 3) color = "yellow";
      else if (rank >= 6 && rank <= 8) color = "orange";
      else if (rank >= 9 && rank <= 10) color = "red";
    } else if (division.startsWith("4div")) {
      if (rank <= 2) color = "green";
      else if (rank >= 3 && rank <= 5) color = "yellow";
    }
    
    return color;
  };

  const getColorStyle = (color: string) => {
    const colorMap: Record<string, string> = {
      green: isDark ? 'rgba(0, 128, 0, 0.3)' : 'rgba(135, 245, 135, 0.4)',
      yellow: isDark ? 'rgba(204, 204, 0, 0.25)' : 'rgba(250, 250, 178, 0.83)',
      orange: isDark ? 'rgba(210, 105, 30, 0.3)' : 'rgba(248, 203, 164, 0.6)',
      red: isDark ? 'rgba(139, 0, 0, 0.3)' : 'rgba(250, 169, 182, 0.5)'
    };
    
    return colorMap[color] || 'transparent';
  };

  // Calculate accuracy score - lower is better
  // Measures average distance between prediction and actual position
  const calculateAccuracyScore = (steamId: string): number | null => {
    const prediction = getPredictionForDivision(steamId, selectedDivision);
    if (!prediction || !prediction.teams.length || !standings.length) return null;
    
    let totalDifference = 0;
    let count = 0;
    
    prediction.teams.forEach(team => {
      const actualStanding = standings.find(s => s.team.teamId === team.teamId);
      if (actualStanding) {
        const diff = Math.abs(team.predictedRank - actualStanding.rank);
        totalDifference += diff;
        count++;
      }
    });
    
    return count > 0 ? +(totalDifference / count).toFixed(2) : null;
  };

  // Sort the standings by player prediction
  const handleSortByPrediction = (steamId: string) => {
    if (sortedBy === steamId) {
      // Toggle direction if already sorting by this column
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // New sort column
      setSortedBy(steamId);
      setSortDirection('asc');
    }
  };
  
  // Sort standings based on current sort settings
  const getSortedStandings = () => {
    if (!standings || standings.length === 0) {
      return [];
    }
    
    const sorted = [...standings];
    
    if (sortedBy === 'rank') {
      // Default sort by current rank
      return sorted.sort((a, b) => 
        sortDirection === 'asc' 
          ? a.rank - b.rank 
          : b.rank - a.rank
      );
    } else {
      // Sort by podcaster prediction
      return sorted.sort((a, b) => {
        const predictionA = getPredictionRank(a.team.teamId, sortedBy) || 999;
        const predictionB = getPredictionRank(b.team.teamId, sortedBy) || 999;
        
        return sortDirection === 'asc'
          ? predictionA - predictionB
          : predictionB - predictionA;
      });
    }
  };

  // Handle row click
  const handleRowClick = (standing: Standing) => {
    // Always select the team, no unselecting
    setSelectedTeam(standing);
    setActiveTab('players');
  };

  // Handle tab click
  const handleTabClick = (tabId: string) => {
    setActiveTab(tabId);
  };

  // Calculate average rating
  const calculateAverageRating = (): string => {
    if (!teamPlayers || teamPlayers.length === 0) return "0.00";
    
    let total = 0;
    let count = 0;
    
    teamPlayers.forEach(player => {
      if (player.hltv2Rating) {
        total += player.hltv2Rating;
        count++;
      }
    });
    
    return count > 0 ? (total / count).toFixed(2) : "0.00";
  };

  if (loading && accounts.length === 0) {
    return (
      <div className={`container-fluid ${isDark ? 'bg-dark text-white' : ''}`} style={{ padding: '1.5rem 10px' }}>
        <div>Loading OGC podcaster information...</div>
      </div>
    );
  }

  if (error && accounts.length === 0) {
    return (
      <div className={`container-fluid ${isDark ? 'bg-dark text-white' : ''}`} style={{ padding: '1.5rem 10px' }}>
        <div className="text-danger">{error}</div>
      </div>
    );
  }

  return (
    <div className={`container-fluid ${isDark ? 'bg-dark text-white' : ''}`} style={{ padding: '1.5rem 10px' }}>
      <h1>OGC Podcaster Predictions</h1>
      <p>Compare predictions from P0se and Broken against actual results</p>
      <hr />
      
      {/* Season Selection */}
      <div className="row mb-3">
        <div className="col-md-4">
          <label>Vælg sæson:</label>
          <div className="dropdown mb-2">
            <button
              className="btn btn-primary dropdown-toggle w-100"
              type="button"
              data-bs-toggle="dropdown"
              aria-expanded="false"
            >
              Sæson {selectedSeason}
            </button>
            <ul className="dropdown-menu w-100">
              {[currentSeason, currentSeason - 1].map((s) => (
                <li key={s}>
                  <button
                    className="dropdown-item text-center"
                    onClick={() => setSelectedSeason(s)}
                  >
                    Sæson {s}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>
        
        {/* Division Selection */}
        <div className="col-md-4">
          <label>Vælg division:</label>
          <div className="dropdown mb-2">
            <button
              className="btn btn-primary dropdown-toggle w-100"
              type="button"
              data-bs-toggle="dropdown"
              aria-expanded="false"
            >
              {selectedDivision
                ? divisionMappings.get(selectedDivision) ?? selectedDivision
                : "Vælg division"}
            </button>
            <ul className="dropdown-menu w-100">
              {divisions.map((d) => (
                <li key={d}>
                  <button
                    className="dropdown-item text-center"
                    onClick={() => {
                      setSelectedDivision(d);
                      setSelectedTeam(null);
                    }}
                  >
                    {divisionMappings.get(d) ?? d}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
      
      {/* Legend */}
      <div className="row mb-4">
        <div className="col-md-12">
          <div className="d-flex flex-wrap gap-3">
            <div className="d-flex align-items-center">
              <div style={{ 
                width: "20px", 
                height: "20px", 
                backgroundColor: getColorStyle('green'),
                borderRadius: "4px",
                marginRight: "8px" 
              }}></div>
              <span>Promotion</span>
            </div>
            <div className="d-flex align-items-center">
              <div style={{ 
                width: "20px", 
                height: "20px", 
                backgroundColor: getColorStyle('yellow'),
                borderRadius: "4px",
                marginRight: "8px" 
              }}></div>
              <span>Playoff</span>
            </div>
            <div className="d-flex align-items-center">
              <div style={{ 
                width: "20px", 
                height: "20px", 
                backgroundColor: getColorStyle('orange'),
                borderRadius: "4px",
                marginRight: "8px" 
              }}></div>
              <span>Relegation Playoff</span>
            </div>
            <div className="d-flex align-items-center">
              <div style={{ 
                width: "20px", 
                height: "20px", 
                backgroundColor: getColorStyle('red'),
                borderRadius: "4px",
                marginRight: "8px" 
              }}></div>
              <span>Relegation</span>
            </div>
          </div>
        </div>
      </div>
      
      {/* Accuracy Scores */}
      {selectedDivision && standings.length > 0 && (
        <div className="row mb-4">
          <div className="col-md-12">
            <div className="alert alert-info">
              <h5>Prediction Accuracy</h5>
              <div className="d-flex flex-wrap gap-4">
                {accounts.map((account, index) => {
                  const steamId = OGC_PODCASTER_IDS[index];
                  const score = calculateAccuracyScore(steamId);
                  return (
                    <div key={steamId}>
                      <strong>{account.playerName}:</strong> {score !== null 
                        ? `${score} (average positions off)`
                        : 'No predictions'}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Main Content Area */}
      <div className="row">
        {/* Comparison Table (Left Side) */}
        <div className={`col-md-${selectedTeam ? '8' : '12'}`}>
          {(!selectedDivision || standings.length === 0) ? (
            <div className="alert alert-info">
              {!selectedDivision 
                ? 'Please select a division to view standings'
                : 'No standings available for this division'}
            </div>
          ) : (
            <div 
              className={`card ${isDark ? 'bg-dark' : ''}`}
              style={{
                border: isDark ? '1px solid #444' : '1px solid #ddd',
              }}
            >
              <div className="card-body">
                <table className={`table ${isDark ? 'table-dark' : 'table-striped'}`}>
                  <thead>
                    <tr>
                      <th 
                        style={{
                          backgroundColor: '#e25623',
                          color: '#fff',
                          width: '10%',
                          cursor: 'pointer'
                        }}
                        onClick={() => {
                          setSortedBy('rank');
                          setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
                        }}
                      >
                        # {sortedBy === 'rank' && (sortDirection === 'asc' ? '▲' : '▼')}
                      </th>
                      <th style={{
                        backgroundColor: '#e25623',
                        color: '#fff',
                        width: '50%',
                      }}>Team</th>
                      {accounts.map(account => (
                        <th 
                          key={account.steamID}
                          style={{
                            backgroundColor: '#e25623',
                            color: '#fff',
                            width: '20%',
                            textAlign: 'center',
                            cursor: 'pointer'
                          }}
                          onClick={() => handleSortByPrediction(account.steamID)}
                        >
                          {account.playerName} {sortedBy === account.steamID && (sortDirection === 'asc' ? '▲' : '▼')}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {getSortedStandings().map((standing) => {
                      const colorName = getStandingColor(selectedDivision, standing.rank);
                      const colorStyle = getColorStyle(colorName);
                      
                      return (
                        <tr 
                          key={standing.team.teamId} 
                          onClick={() => handleRowClick(standing)}
                          style={{ 
                            cursor: 'pointer',
                            backgroundColor: selectedTeam?.team.teamId === standing.team.teamId 
                              ? (isDark ? '#3a4049' : '#e9ecef') 
                              : undefined
                          }}
                        >
                          <td style={{
                            backgroundColor: colorStyle,
                            fontWeight: "bold",
                            borderRadius: "4px",
                            width: "40px",
                            textAlign: "center"
                          }}>
                            {standing.rank}
                          </td>
                          
                          <td>
                            <div className="d-flex align-items-center">
                              <Link to={`/team/${standing.team.teamId}?season=${selectedSeason}`}>
                                <img 
                                  src={standing.team.teamLogo} 
                                  alt={standing.team.teamName} 
                                  style={{ width: 25, height: 25, marginRight: 8, cursor: 'pointer' }} 
                                />
                              </Link>
                              {standing.team.teamName}
                            </div>
                          </td>
                          
                          {accounts.map((account, index) => {
                            const steamId = OGC_PODCASTER_IDS[index];
                            const predictionRank = getPredictionRank(standing.team.teamId, steamId);
                            
                            // Calculate difference between prediction and actual
                            const difference = predictionRank 
                              ? predictionRank - standing.rank 
                              : null;
                            
                            return (
                              <td 
                                key={steamId}
                                style={{
                                  textAlign: "center"
                                }}
                              >
                                {predictionRank ? (
                                  <div>
                                    <span className="fw-bold">{predictionRank}</span>
                                    <span 
                                      className={`ms-2 badge ${difference === 0 ? 'bg-success' : 'bg-danger'}`}
                                      title={difference === 0 ? 'Correct prediction!' : 
                                        `${Math.abs(difference || 0)} position${Math.abs(difference || 0) !== 1 ? 's' : ''} ${(difference || 0) > 0 ? 'too high' : 'too low'}`}
                                    >
                                      {difference === 0 ? '0' : ((difference || 0) > 0 ? '+' : '') + (difference || 0)}
                                    </span>
                                  </div>
                                ) : (
                                  <span className="text-muted">-</span>
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
        
        {/* Team Details Panel (Right Side) */}
        {selectedTeam && (
          <div className="col-md-4">
            <div className={`card mb-3 ${isDark ? 'bg-dark text-white border-secondary' : ''}`}>
                              <div className="card-body text-center">
                <Link to={`/team/${selectedTeam.team.teamId}?season=${selectedSeason}`} className={`text-decoration-none ${isDark ? 'text-white' : 'text-dark'}`}>
                  <h3>{selectedTeam.team.teamName}</h3>
                  <img 
                    src={selectedTeam.team.teamLogo} 
                    alt={selectedTeam.team.teamName} 
                    style={{ maxWidth: '150px', maxHeight: '150px', margin: '10px auto', cursor: 'pointer' }}
                  />
                </Link>
                <div className="d-flex justify-content-between">
                  <span>Current Rank: <strong>{selectedTeam.rank}</strong></span>
                  <span>Points: <strong>{selectedTeam.points}</strong></span>
                </div>
                <div className="d-flex justify-content-between mt-2">
                  <span>Wins/Losses: <strong>{selectedTeam.wins}/{selectedTeam.gamesPlayed - selectedTeam.wins}</strong></span>
                </div>
                
                {/* Podcaster predictions for this team */}
                <div className="mt-3">
                  <h5>Predictions</h5>
                  <div className="row">
                    {accounts.map((account, index) => {
                      const steamId = OGC_PODCASTER_IDS[index];
                      const predictionRank = getPredictionRank(selectedTeam.team.teamId, steamId);
                      const difference = predictionRank 
                        ? predictionRank - selectedTeam.rank 
                        : null;
                      
                      return (
                        <div className="col-6" key={account.steamID}>
                          <div className={`card mb-2 ${isDark ? 'bg-secondary text-white' : ''}`}>
                            <div className="card-body p-2">
                              <h6>{account.playerName}</h6>
                              {predictionRank ? (
                                <>
                                  <span className="fw-bold">Rank: {predictionRank}</span>
                                  {difference !== null && (
                                    <span 
                                      className={`ms-2 d-block badge ${difference === 0 ? 'bg-success' : 'bg-danger'}`}
                                    >
                                      {difference === 0 ? 'Correct!' : `${(difference || 0) > 0 ? '+' : ''}${difference} off`}
                                    </span>
                                  )}
                                </>
                              ) : (
                                <span className="text-muted">No prediction</span>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
            
            {/* Tabs for Team Details */}
            <div className={`card ${isDark ? 'bg-dark text-white border-secondary' : ''}`}>
              <div className={`card-header ${isDark ? 'bg-dark border-secondary' : ''}`}>
                <ul className="nav nav-tabs card-header-tabs">
                  {tabs.map(tab => (
                    <li className="nav-item" key={tab.id}>
                      <button 
                        className={`nav-link ${activeTab === tab.id ? (isDark ? 'active bg-secondary text-white' : 'active') : (isDark ? 'text-white' : '')}`}
                        onClick={() => handleTabClick(tab.id)}
                      >
                        {tab.title}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="card-body">
                {teamDetailsLoading ? (
                  <div className="text-center py-3">
                    <div className="spinner-border" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </div>
                ) : (
                  <>
                    {/* Players Tab */}
                    {activeTab === 'players' && (
                      <div>
                        <h5 className="text-center">Average Rating: {calculateAverageRating()}</h5>
                        <div className="row g-2 justify-content-center">
                          {teamPlayers.length > 0 ? (
                            teamPlayers.map((player, index) => (
                              <div className="col-md-6" key={player.steamID || index}>
                                <div className={`card p-2 ${isDark ? 'bg-dark text-white' : ''}`}>
                                  <div className="text-center">
                                    <Link to={`/player/${player.steamID}`} className={`text-decoration-none ${isDark ? 'text-white' : 'text-dark'}`}>
                                      <PlayerImage id={player.steamID} width={60} />
                                      <div className="mt-2">
                                        <strong>
                                          {player.account ? player.account.playerName : player.steamID}
                                        </strong>
                                      </div>
                                      <div className={`small ${isDark ? 'text-light' : 'text-muted'}`}>
                                        Rating: {player.hltv2Rating?.toFixed(2) || 'N/A'}
                                      </div>
                                      <div className={`small ${isDark ? 'text-light' : 'text-muted'}`}>
                                        {player.gamesPlayed} - Kampe spillet
                                      </div>
                                    </Link>
                                  </div>
                                </div>
                              </div>
                            ))
                          ) : (
                            <div className="text-center py-3">
                              <p>No player data available</p>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                    
                    {/* Matches Tab */}
                    {activeTab === 'matches' && (
                      <div>
                        {teamMatches.length > 0 ? (
                          <MatchTable 
                            matches={teamMatches} 
                            compact={true} 
                            season={selectedSeason}
                            predictMode={true} // Use the new predictMode prop
                          />
                        ) : (
                          <div className="text-center py-3">
                            <p>No match data available</p>
                          </div>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default OGCPage;